import { useLayoutEffect, useRef } from "react";

import { CLUE_MAX_LENGTH } from "./CrosswordPuzzle.constants";
import {
  clueColumn,
  clueColumnTitle,
  clueListStyle,
  clueText,
  clueTextarea,
} from "./CrosswordPuzzle.styles";

import type { CrosswordClue } from "@/types/models/Exportables.interface";

interface CrosswordClueListProps {
  title: string;
  clues: CrosswordClue[];
  direction: "across_clues" | "down_clues";
  startNumber: number;
  isStudent: boolean;
  onClueChange: (
    direction: "across_clues" | "down_clues",
    idx: number,
    value: string,
  ) => void;
}

const autoResize = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const CrosswordClueList = ({
  title,
  clues,
  direction,
  startNumber,
  isStudent,
  onClueChange,
}: CrosswordClueListProps) => {
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  useLayoutEffect(() => {
    textareaRefs.current.forEach(autoResize);
  }, [clues]);

  return (
    <div css={clueColumn}>
      <h3 css={clueColumnTitle}>{title}</h3>
      <ol css={clueListStyle}>
        {clues.map((clue, idx) => {
          const num = startNumber + idx;
          return (
            <li key={`${direction}-${idx}`} value={num}>
              {isStudent ? (
                <p css={clueText} aria-label={`${title} clue ${num}`}>
                  {clue.clue}
                </p>
              ) : (
                <textarea
                  ref={(el) => {
                    textareaRefs.current[idx] = el;
                  }}
                  css={clueTextarea}
                  value={clue.clue}
                  maxLength={CLUE_MAX_LENGTH}
                  onChange={(e) => {
                    onClueChange(
                      direction,
                      idx,
                      e.target.value.replace(/\n/g, ""),
                    );
                    autoResize(e.target as HTMLTextAreaElement);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  aria-label={`${title} clue ${num}`}
                  data-qa-selector={`crossword-${direction.replace("_clues", "")}-clue-${num}`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default CrosswordClueList;
